package com.steamstreet.vegasful.html.components

import kotlinx.html.A

/**
 * The `rel` every link that leaves vegasful.com carries, decided in one place.
 *
 * Two separate problems share one attribute, which is why they share one file:
 *
 * - **Ranking.** The site links out to fifty-odd venue, resort and ticket-vendor domains whose
 *   content nobody here writes or reviews. A followed link is an endorsement as far as a search
 *   engine is concerned, and endorsing every URL a scraper happened to find is not something
 *   anyone decided to do.
 * - **Reverse tabnabbing.** A link opened in another browsing context hands that context a live
 *   `window.opener` unless `noopener` says otherwise, and the destination can then navigate the
 *   page the reader came from. It applies to a named target such as `target="social"` exactly as
 *   it applies to `target="_blank"`.
 *
 * `noreferrer` is deliberately **not** included. It suppresses the `Referer` header, which is how
 * a ticket vendor sees that the click came from us; that is the one piece of outbound measurement
 * we get for free, and it is worth more than the little `noreferrer` adds on top of `noopener`.
 */
public enum class LinkOrigin {
    /**
     * A destination we chose: a map link built from an address, an app-store listing, a link
     * written into one of our own articles. Still `nofollow` — we are not in the business of
     * passing ranking signal off-site — but not `ugc`, because a person here picked it.
     */
    EDITORIAL,

    /**
     * A destination that arrived with the data: a ticket URL, a venue's own website, a social
     * handle, a link inside a scraped or model-written description. `ugc` is the honest
     * description of it, and it is what tells a search engine the difference between the two
     * kinds of link on the same page.
     */
    THIRD_PARTY
}

/**
 * True when [href] leaves vegasful.com.
 *
 * Internal links on this site are rendered as paths (`/events/…`), so anything that names a host
 * at all is the interesting case. A URL with a scheme we do not follow — `tel:`, `mailto:`, a bare
 * `#` — is not an outbound link in the sense this file cares about and gets nothing.
 */
public fun isOffSite(href: String?): Boolean {
    val url = href?.trim().orEmpty()
    if (url.isEmpty()) return false

    val host = when {
        // Protocol-relative: `//example.com/path`.
        url.startsWith("//") -> url.drop(2)

        else -> {
            val schemeEnd = url.indexOf(':')
            if (schemeEnd <= 0) return false
            val scheme = url.substring(0, schemeEnd).lowercase()
            if (scheme != "http" && scheme != "https") return false
            if (!url.startsWith("$scheme://", ignoreCase = true)) return false
            url.substring(schemeEnd + 3)
        }
    }.takeWhile { it != '/' && it != '?' && it != '#' }
        .substringAfterLast('@')
        .substringBefore(':')
        .lowercase()

    if (host.isEmpty()) return false

    return INTERNAL_HOSTS.none { host == it || host.endsWith(".$it") }
}

/**
 * The `rel` value for [href], or null when the link stays on the site and needs none.
 */
public fun outboundRel(href: String?, origin: LinkOrigin = LinkOrigin.THIRD_PARTY): String? {
    if (!isOffSite(href)) return null
    return when (origin) {
        LinkOrigin.EDITORIAL -> "nofollow noopener"
        LinkOrigin.THIRD_PARTY -> "nofollow ugc noopener"
    }
}

/**
 * Give this anchor the `rel` its [href] deserves, if it deserves one.
 *
 * Called from the shared link components rather than from each page, so a new page that renders a
 * ticket button through [pill] or a button helper is covered without anybody remembering to do
 * anything.
 */
public fun A.markOutbound(href: String?, origin: LinkOrigin = LinkOrigin.THIRD_PARTY) {
    outboundRel(href, origin)?.let { attributes["rel"] = it }
}

/**
 * Add the same `rel` to every off-site anchor inside a block of HTML we did not build.
 *
 * Descriptions and article bodies are emitted unescaped, so any `<a>` a scraper or the model left
 * in one is a live, followed link that no call site ever sees. This rewrites only the opening tag
 * of an anchor and leaves every other byte alone — deliberately, rather than parsing and
 * re-serialising the fragment, because a parser would also silently normalise markup that is
 * currently rendering correctly.
 *
 * An anchor that already carries a `rel` is left as it is; the only source of one today is markup
 * somebody wrote on purpose.
 */
public fun htmlWithOutboundRel(html: String, origin: LinkOrigin = LinkOrigin.THIRD_PARTY): String {
    // Cheap enough to run on every description, but most descriptions have no anchors at all.
    if (!html.contains("<a", ignoreCase = true)) return html

    return ANCHOR_TAG.replace(html) { match ->
        val tag = match.value
        if (REL_ATTRIBUTE.containsMatchIn(tag)) return@replace tag

        val href = HREF_ATTRIBUTE.find(tag)?.let { it.groupValues[2] + it.groupValues[3] + it.groupValues[4] }
        val rel = outboundRel(href, origin) ?: return@replace tag

        val selfClosing = tag.endsWith("/>")
        val body = tag.dropLast(if (selfClosing) 2 else 1).trimEnd()
        "$body rel=\"$rel\"${if (selfClosing) "/>" else ">"}"
    }
}

/** Hosts that are us. `localhost` keeps a locally served page from looking like fifty outbound links. */
private val INTERNAL_HOSTS = listOf("vegasful.com", "localhost")

private val ANCHOR_TAG = Regex("""<a\b[^>]*>""", RegexOption.IGNORE_CASE)
private val REL_ATTRIBUTE = Regex("""\brel\s*=""", RegexOption.IGNORE_CASE)
private val HREF_ATTRIBUTE = Regex("""\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))""", RegexOption.IGNORE_CASE)
